import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { GridColDef, GridSortDirection } from '@mui/x-data-grid-pro'
import { OptionsState } from 'particles/Dropdown'
import { getFilterOrSortTooltipMenuItem } from './FilterOrSortTooltip'
import {
    isAToZField,
    isHighestToLowestField,
    isSortable,
    isLatestToOldestField,
    isOppositeSortField,
    isYesToNoField,
} from 'components/utils'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { SORT_DIRECTION } from 'components/utils/filterAndSort'
import Menu from 'particles/Menu'
import MenuItem from 'particles/MenuItem'

interface SortState {
    sort: GridSortDirection
    sortDirectionLabel: string
}

interface ColumnSortTooltipProps {
    anchorEl: HTMLDivElement | null
    onClose: () => void
    onChange: (option: OptionsState, sortOrColumn?: string | SortState) => void
    column: GridColDef
    sortItem: {
        field: string
        sort: GridSortDirection
        fieldLabel: string
        sortDirectionLabel: string
    }
}

interface FilterAndSortButtonContextProps {
    sortable: boolean
    ascendingText: string
    descendingText: string
}

const ColumnSortTooltip: React.FC<ColumnSortTooltipProps> = ({ anchorEl, onClose, column, onChange, sortItem }) => {
    const [t] = useTranslation()
    const { headerName, field: value } = column || { headerName: '', field: '' }

    const { field: sortField, sort } = sortItem || {}

    const [field, setField] = useState<OptionsState>({ value: value, label: headerName as string })

    useEffect(() => {
        setField({ label: headerName as string, value: value })
    }, [])

    const handleSortValueChange = (option: OptionsState) => {
        setField(option)
    }

    const handleSortDirectionChange = (sort: GridSortDirection, sortDirectionLabel: string) => {
        const fieldValues = {
            value: value,
            label: headerName as string,
        }
        const sortValues = {
            sort: sort,
            sortDirectionLabel: sortDirectionLabel,
        }
        // Using this structure as a workaround to the fact that the onChange function is expecting a similar structure to filter in FilterSortChip.tsx
        field && onChange(fieldValues, sortValues)
    }
    const isAToZSort = isAToZField(field.value)
    const isPreviousSortAToZ = isAToZField(sortField)

    const isHighestToLowest = isHighestToLowestField(field.value)

    const isOppositeSort = isOppositeSortField(field.value)

    const isLatestToOldest = isLatestToOldestField(field.value)
    const isPreviousSortOpposite = isOppositeSortField(sortField)

    const isYesToNo = isYesToNoField(field.value)
    const isPreviousSortYesToNo = isYesToNoField(sortField)

    const switchedFromNumericFieldToNumericField = isOppositeSort && isPreviousSortOpposite

    const switchedFromAlphabetFieldToAlphabetField = isAToZSort && isPreviousSortAToZ

    const switchedFromYesToNoFieldToYesToNoField = isYesToNo && isPreviousSortYesToNo

    const FilterAndSortButtonContext: FilterAndSortButtonContextProps = useMemo(() => {
        let context = {
            sortable: isSortable(field.value),
            ascendingText: '',
            descendingText: '',
        }

        if (isAToZSort) {
            context = {
                ...context,
                ascendingText: t('button.sortAscendingAlphabet'),
                descendingText: t('button.sortDescendingAlphabet'),
            }
        } else if (isHighestToLowest) {
            context = {
                ...context,
                ascendingText: t('button.sortHighestLowest'),
                descendingText: t('button.sortLowestHighest'),
            }
        } else if (isLatestToOldest) {
            context = {
                ...context,
                ascendingText: t('button.sortLatestOldest'),
                descendingText: t('button.sortOldestLatest'),
            }
        }
        if (field.value === FIELDS_TYPE.MEET_REQUIREMENT) {
            context = {
                ...context,
                ascendingText: t('button.sortYesToNo'),
                descendingText: t('button.sortNoToYes'),
            }
        }
        if (field.value === FIELDS_TYPE.HIGHLIGHT) {
            context = {
                ...context,
                ascendingText: t('button.sortSavedToNotSaved'),
                descendingText: t('button.sortNotSavedToSaved'),
            }
        }
        return context
    }, [field.value, sortField])

    const sortValues = useMemo(() => {
        const sortOrder =
            sort &&
            (switchedFromAlphabetFieldToAlphabetField ||
                switchedFromNumericFieldToNumericField ||
                switchedFromYesToNoFieldToYesToNoField)
                ? sort
                : isOppositeSort
                  ? SORT_DIRECTION.DESC
                  : SORT_DIRECTION.ASC

        const sortDirectionLabel =
            isOppositeSort && sortOrder === SORT_DIRECTION.ASC
                ? FilterAndSortButtonContext.descendingText
                : isOppositeSort && sortOrder === SORT_DIRECTION.DESC
                  ? FilterAndSortButtonContext.ascendingText
                  : sortOrder === SORT_DIRECTION.ASC
                    ? FilterAndSortButtonContext.ascendingText
                    : FilterAndSortButtonContext.descendingText

        return {
            sort: sortOrder,
            sortDirectionLabel,
        }
    }, [field.value])

    useEffect(() => {
        const fieldValues = {
            value: field.value as string,
            label: field.label as string,
        }

        field && onChange(fieldValues, sortValues)
    }, [sortValues])

    const sortMenuitems = getFilterOrSortTooltipMenuItem({
        title: t('dropdownMenu.sortByHeader'),
        isSort: true,
        onChange: handleSortValueChange,
    })

    const sortAscendingValue = isOppositeSort ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC
    const sortDescendingValue = isOppositeSort ? SORT_DIRECTION.ASC : SORT_DIRECTION.DESC

    return (
        <>
            <Menu size="s" width={216} maxHeight={360} open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={onClose}>
                <MenuItem.Dropdown text={t('dropdownMenu.selectValueTypeHeader')} value={sortField}>
                    {sortMenuitems}
                </MenuItem.Dropdown>
                <MenuItem.Dropdown text={t('dropdownMenu.selectSortOrderHeader')} value={sort}>
                    <MenuItem.Text
                        size="s"
                        icon="sortAscending"
                        text={FilterAndSortButtonContext.ascendingText}
                        onClick={() =>
                            handleSortDirectionChange(sortAscendingValue, FilterAndSortButtonContext.ascendingText)
                        }
                        value={sortAscendingValue}
                    />
                    <MenuItem.Text
                        size="s"
                        icon="sortDescending"
                        text={FilterAndSortButtonContext.descendingText}
                        onClick={() =>
                            handleSortDirectionChange(sortDescendingValue, FilterAndSortButtonContext.descendingText)
                        }
                        value={sortDescendingValue}
                    />
                </MenuItem.Dropdown>
            </Menu>
        </>
    )
}

export default ColumnSortTooltip
